import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { FormClose } from 'grommet-icons';
import uniqid from 'uniqid';
import { AppButton } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ProductOfferingType } from '@RedflagAI/node-utility/interface';
import {
  ProductFormSingleTextInput,
  ProductFormDropdownSelect,
} from '@Components/Partial/Product/NewSearch';

const EXTRA_ITEM_DEFAULTS = {
  kind: 'discount',
  valueType: 'amount',
  value: '',
  description: '',
  frequency: 'monthly',
  productKey: null,
};

const ProductSummaryCard = ({
  product, config, index, onEdit, theme,
}) => {
  const { page } = theme;
  const planName = config.planMode === 'existing'
    ? (config.planLabel || config.planId || 'Selected plan')
    : (config.customPlan?.name || 'Custom plan');

  const renderDataHighlights = () => {
    const type = product.productType;
    const data = config.customPlan?.data || {};
    if (type === ProductOfferingType.CID) {
      return (
        <Box gap="0.2rem">
          {data.uploader && (
            <Text size="0.78rem" color={page.secondaryText}>
              {`Uploader: ${data.uploader}`}
            </Text>
          )}
          {(data.setupFeeAmount !== '' && data.setupFeeAmount !== undefined) && (
            <Text size="0.78rem" color={page.secondaryText}>
              {`Setup fee: $${data.setupFeeAmount}`}
            </Text>
          )}
          {(data.monthlyFeeAmount !== '' && data.monthlyFeeAmount !== undefined) && (
            <Text size="0.78rem" color={page.secondaryText}>
              {`Monthly fee: $${data.monthlyFeeAmount}`}
            </Text>
          )}
          {(data.revenueSharePercent !== '' && data.revenueSharePercent !== undefined) && (
            <Text size="0.78rem" color={page.secondaryText}>
              {`Rev share: ${data.revenueSharePercent}%`}
              {data.revenueShareStartMonth > 0
                ? ` (from month ${data.revenueShareStartMonth})` : ''}
            </Text>
          )}
        </Box>
      );
    }
    if (type === ProductOfferingType.SOCIAL) {
      return (
        <Box gap="0.2rem">
          <Text size="0.78rem" color={page.secondaryText}>
            {`Per platform: $${data.defaultPerPlatformAmount ?? 10}`}
          </Text>
        </Box>
      );
    }
    if (config.planMode === 'custom' || product.mode === 'new') {
      return (
        <Box gap="0.2rem">
          {(data.setupFee !== '' && data.setupFee !== undefined) && (
            <Text size="0.78rem" color={page.secondaryText}>
              {`Setup fee: $${data.setupFee}`}
            </Text>
          )}
          {(data.amount !== '' && data.amount !== undefined) && (
            <Text size="0.78rem" color={page.secondaryText}>{`Amount: $${data.amount}`}</Text>
          )}
          {data.frequency && (
            <Text size="0.78rem" color={page.secondaryText}>{`Frequency: ${data.frequency}`}</Text>
          )}
        </Box>
      );
    }
    return null;
  };

  const configHighlights = () => {
    const type = product.productType;
    const cfg = config.config || {};
    if (type === ProductOfferingType.CID) {
      const channels = (cfg.channels || []).filter((c) => c.trim());
      return (
        <Text size="0.78rem" color={page.secondaryText}>{`Channels: ${channels.length}`}</Text>
      );
    }
    if (type === ProductOfferingType.SOCIAL) {
      return (
        <Text size="0.78rem" color={page.secondaryText}>
          {`Platforms: ${(cfg.platforms || []).join(', ') || 'none'}`}
        </Text>
      );
    }
    return null;
  };

  return (
    <Box
      pad="1rem"
      round="var(--app-radius)"
      border={{ color: theme.page.border, size: '1px' }}
      gap="0.5rem"
    >
      <Box direction="row" justify="between" align="start">
        <Box gap="0.2rem">
          <Text size="0.9rem" weight={600} color={page.primaryText}>{product.productLabel}</Text>
          <Text size="0.8rem" color={page.secondaryText}>{`Plan: ${planName}`}</Text>
          {renderDataHighlights()}
          {configHighlights()}
        </Box>
        <AppButton
          overrideHover
          level="dynamicSmall"
          bgColor="transparent"
          color={theme.button.primaryBg}
          label="Edit"
          onClick={() => onEdit(index)}
        />
      </Box>
    </Box>
  );
};

ProductSummaryCard.propTypes = {
  product: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

const ExtraItemRow = ({
  small, item, index, productKeyOptions, onChange, onRemove, theme,
}) => {
  const { page } = theme;
  const isPercent = item.valueType === 'percent';

  return (
    <Box
      pad="0.75rem"
      gap="0.5rem"
      round="var(--app-radius)"
      border={{ color: page.border, size: '1px' }}
    >
      <Box direction="row" gap="0.5rem" wrap>
        <Box flex={{ min: '120px' }} gap="0.2rem">
          <Text size="0.78rem" weight={600} color={page.primaryText}>Kind</Text>
          <ProductFormDropdownSelect
            selectable
            simpleOptions={['discount', 'charge']}
            handleFormValues={(val) => onChange(index, { ...item, kind: val })}
            value={item.kind}
            focusHighlight={page.focusHighlight}
          />
        </Box>
        <Box flex={{ min: '120px' }} gap="0.2rem">
          <Text size="0.78rem" weight={600} color={page.primaryText}>Value Type</Text>
          <ProductFormDropdownSelect
            selectable
            simpleOptions={['amount', 'percent']}
            handleFormValues={(val) => onChange(index, { ...item, valueType: val })}
            value={item.valueType}
            focusHighlight={page.focusHighlight}
          />
        </Box>
        <Box flex={{ min: '100px' }} gap="0.2rem">
          <Text size="0.78rem" weight={600} color={page.primaryText}>
            {`Value ${isPercent ? '(%)' : '($)'}`}
          </Text>
          <ProductFormSingleTextInput
            small={small}
            type="number"
            focusHighlight={page.focusHighlight}
            value={item.value}
            handleFormValues={(val) => onChange(index, { ...item, value: val })}
            placeholder="0"
            min={0}
            max={isPercent ? 100 : undefined}
          />
        </Box>
        <Box flex={{ min: '120px' }} gap="0.2rem">
          <Text size="0.78rem" weight={600} color={page.primaryText}>Frequency</Text>
          <ProductFormDropdownSelect
            selectable
            simpleOptions={['monthly', 'one_time']}
            handleFormValues={(val) => onChange(index, { ...item, frequency: val })}
            value={item.frequency}
            focusHighlight={page.focusHighlight}
          />
        </Box>
        <Box flex={{ min: '140px' }} gap="0.2rem">
          <Text size="0.78rem" weight={600} color={page.primaryText}>Product (optional)</Text>
          <ProductFormDropdownSelect
            selectable
            keepLabel
            simpleOptions={['All products', ...productKeyOptions]}
            handleFormValues={(val) => onChange(index, {
              ...item,
              productKey: val === 'All products' ? null : val,
            })}
            value={item.productKey || 'All products'}
            label={item.productKey || 'All products'}
            focusHighlight={page.focusHighlight}
          />
        </Box>
      </Box>
      <Box gap="0.2rem">
        <Box direction="row" gap="0.2rem">
          <Text size="0.78rem" weight={600} color={page.primaryText}>Description</Text>
          <Text size="0.78rem" color="var(--app-error)">*</Text>
        </Box>
        <Box direction="row" gap="0.5rem" align="center">
          <Box flex>
            <ProductFormSingleTextInput
              small={small}
              focusHighlight={page.focusHighlight}
              value={item.description}
              handleFormValues={(val) => onChange(index, { ...item, description: val })}
              placeholder="e.g. Early bird discount"
            />
          </Box>
          <AppButton
            overrideHover
            level="dynamicSmall"
            bgColor="var(--app-error)"
            color="white"
            label={(
              <Box direction="row" align="center" gap="0.25rem">
                <Text size="0.9rem">Remove</Text>
                <FormClose size="0.9rem" />
              </Box>
            )}
            onClick={() => onRemove(index)}
          />
        </Box>
      </Box>
    </Box>
  );
};

ExtraItemRow.propTypes = {
  small: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  productKeyOptions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

const Step4PricingSummary = ({
  small,
  selectedProducts,
  productConfigs,
  extraItems,
  setExtraItems,
  onEditProduct,
}) => {
  const { theme } = useAppTheme();
  const { page, button } = theme;

  const productKeyOptions = selectedProducts
    .map((p) => p.productKey)
    .filter(Boolean);

  const addItem = () => setExtraItems(
    (prev) => [...prev, { ...EXTRA_ITEM_DEFAULTS, id: uniqid() }],
  );
  const updateItem = (idx, updated) => setExtraItems(
    (prev) => prev.map((it, i) => (i === idx ? updated : it)),
  );
  const removeItem = (idx) => setExtraItems((prev) => prev.filter((_, i) => i !== idx));

  return (
    <Box gap="1.25rem">
      <Box gap="0.75rem">
        <Text size="0.95rem" weight={700} color={page.primaryText}>Product Summary</Text>
        {selectedProducts.map((product, idx) => (
          <ProductSummaryCard
            key={product.productLabel}
            product={product}
            config={productConfigs[idx] || {}}
            index={idx}
            onEdit={onEditProduct}
            theme={theme}
          />
        ))}
      </Box>
      <Box gap="0.75rem">
        <Box direction="row" justify="between" align="center">
          <Text size="0.95rem" weight={700} color={page.primaryText}>
            Extra Items (optional)
          </Text>
          <AppButton
            overrideHover
            level="dynamicSmall"
            bgColor={button.primaryBg}
            color="white"
            label="+ Add item"
            onClick={addItem}
          />
        </Box>
        {extraItems.length === 0 && (
          <Text size="0.82rem" color={page.secondaryText}>
            No extra items. Add discounts or additional charges if needed.
          </Text>
        )}
        {extraItems.map((item, idx) => (
          <ExtraItemRow
            key={item.id}
            small={small}
            item={item}
            index={idx}
            productKeyOptions={productKeyOptions}
            onChange={updateItem}
            onRemove={removeItem}
            theme={theme}
          />
        ))}
      </Box>
    </Box>
  );
};

Step4PricingSummary.propTypes = {
  small: PropTypes.bool.isRequired,
  selectedProducts: PropTypes.array.isRequired,
  productConfigs: PropTypes.array.isRequired,
  extraItems: PropTypes.array.isRequired,
  setExtraItems: PropTypes.func.isRequired,
  onEditProduct: PropTypes.func.isRequired,
};

export default Step4PricingSummary;
